import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, DollarSign, Eye, Heart, Package, ShoppingBag, BarChart3, Wallet } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, color = 'emerald' }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg bg-${color}-50 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
      <div>
        <p className="text-xs text-stone-500 uppercase tracking-wider">{label}</p>
        <p className="text-xl font-bold text-stone-900">{value}</p>
        {sub && <p className="text-[10px] text-stone-400">{sub}</p>}
      </div>
    </div>
  </div>
);

const SellerAnalyticsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !user?.id) {
      navigate('/login');
      return;
    }
    fetchAnalytics();
  }, [isAuthenticated, user?.id, authLoading]);

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/seller/analytics/${user.id}`);
      if (res.ok) {
        const result = await res.json();
        setData(result);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-stone-50 p-4">
        <p className="text-stone-500 text-center mt-20">Could not load analytics</p>
      </div>
    );
  }

  const { summary, chart_data, top_by_views, order_status } = data;

  // Revenue chart
  const revenueChartData = {
    labels: chart_data.map(d => {
      const date = new Date(d.date);
      return `${date.getDate()}/${date.getMonth() + 1}`;
    }),
    datasets: [{
      label: 'Revenue ($)',
      data: chart_data.map(d => d.revenue),
      borderColor: '#059669',
      backgroundColor: 'rgba(5, 150, 105, 0.08)',
      fill: true,
      tension: 0.4,
      pointRadius: 0,
      pointHitRadius: 10,
      borderWidth: 2,
    }],
  };

  const revenueChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `$${ctx.parsed.y.toFixed(2)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, maxTicksLimit: 8, color: '#9ca3af' },
      },
      y: {
        grid: { color: '#f3f4f6' },
        ticks: { font: { size: 10 }, callback: (v) => `$${v}`, color: '#9ca3af' },
        beginAtZero: true,
      },
    },
  };

  // Order status doughnut
  const statusLabels = Object.keys(order_status);
  const statusColors = {
    pending: '#f59e0b',
    shipped: '#3b82f6',
    delivered: '#8b5cf6',
    completed: '#10b981',
    cancelled: '#ef4444',
  };

  const doughnutData = {
    labels: statusLabels.map(s => s.charAt(0).toUpperCase() + s.slice(1)),
    datasets: [{
      data: statusLabels.map(s => order_status[s]),
      backgroundColor: statusLabels.map(s => statusColors[s] || '#9ca3af'),
      borderWidth: 0,
    }],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { font: { size: 11 }, padding: 12, usePointStyle: true, pointStyleWidth: 8 },
      },
    },
  };

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="seller-analytics-page">
      {/* Header */}
      <div className="bg-minted-900 text-white px-4 py-5">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate(-1)} data-testid="analytics-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-display text-lg">Seller Analytics</h1>
        </div>
        <p className="text-minted-300 text-xs">Your sales performance at a glance</p>
      </div>

      <div className="px-4 py-4 space-y-4 max-w-2xl mx-auto">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-3">
          <StatCard icon={DollarSign} label="Total Revenue" value={`$${summary.total_revenue.toFixed(2)}`} sub={`$${summary.revenue_30d.toFixed(2)} last 30d`} />
          <StatCard icon={ShoppingBag} label="Total Orders" value={summary.total_orders} sub={`${summary.orders_30d} last 30d`} color="blue" />
          <StatCard icon={Package} label="Active Listings" value={summary.active_listings} sub={`${summary.sold_count} sold`} color="purple" />
          <StatCard icon={Eye} label="Total Views" value={summary.total_views.toLocaleString()} sub={`${summary.total_likes} likes`} color="amber" />
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl p-3 shadow-sm border border-stone-100 text-center">
            <p className="text-xs text-stone-500">Conversion</p>
            <p className="text-lg font-bold text-emerald-600">{summary.conversion_rate}%</p>
          </div>
          <div className="bg-white rounded-xl p-3 shadow-sm border border-stone-100 text-center">
            <p className="text-xs text-stone-500">Avg Order</p>
            <p className="text-lg font-bold text-stone-900">${summary.avg_order_value}</p>
          </div>
          <div className="bg-white rounded-xl p-3 shadow-sm border border-stone-100 text-center">
            <p className="text-xs text-stone-500">Wallet</p>
            <p className="text-lg font-bold text-emerald-600">${summary.wallet_available.toFixed(2)}</p>
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100" data-testid="revenue-chart">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <h3 className="font-medium text-stone-900 text-sm">Revenue (Last 30 Days)</h3>
          </div>
          <div className="h-48">
            <Line data={revenueChartData} options={revenueChartOptions} />
          </div>
        </div>

        {/* Order Status + Top Products side by side on larger screens */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Order Status */}
          {statusLabels.length > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100" data-testid="order-status-chart">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-blue-600" />
                <h3 className="font-medium text-stone-900 text-sm">Order Status</h3>
              </div>
              <div className="h-48">
                <Doughnut data={doughnutData} options={doughnutOptions} />
              </div>
            </div>
          )}

          {/* Top Products by Views */}
          {top_by_views.length > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100" data-testid="top-products">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-4 h-4 text-amber-600" />
                <h3 className="font-medium text-stone-900 text-sm">Top Products</h3>
              </div>
              <div className="space-y-2">
                {top_by_views.map((p, i) => (
                  <div
                    key={p.id}
                    className="flex items-center gap-3 cursor-pointer hover:bg-stone-50 rounded-lg p-1.5 -mx-1.5 transition-colors"
                    onClick={() => navigate(`/product/${p.id}`)}
                  >
                    <span className="text-xs font-bold text-stone-400 w-4">{i + 1}</span>
                    <div className="w-8 h-8 rounded bg-stone-100 overflow-hidden flex-shrink-0">
                      {(p.thumbnail || (p.images && p.images[0])) && (
                        <img src={p.thumbnail || p.images[0]} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-stone-900 truncate">{p.title}</p>
                      <p className="text-[10px] text-stone-400">${p.price} {p.sold ? '(Sold)' : ''}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-medium text-stone-700">{p.views || 0}</p>
                      <p className="text-[10px] text-stone-400">views</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Wallet Summary */}
        <div className="bg-gradient-to-r from-emerald-800 to-emerald-900 rounded-xl p-4 text-white" data-testid="wallet-summary">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="w-4 h-4" />
            <h3 className="font-medium text-sm">Wallet Summary</h3>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-emerald-300 text-[10px] uppercase">Available</p>
              <p className="text-xl font-bold">${summary.wallet_available.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-emerald-300 text-[10px] uppercase">Pending</p>
              <p className="text-xl font-bold">${summary.wallet_pending.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-emerald-300 text-[10px] uppercase">Total Earned</p>
              <p className="text-xl font-bold">${summary.wallet_total_earnings.toFixed(2)}</p>
            </div>
          </div>
          <Button
            className="mt-3 bg-white text-emerald-900 hover:bg-emerald-50 w-full text-xs h-9"
            onClick={() => navigate('/wallet')}
            data-testid="go-to-wallet-btn"
          >
            Go to Wallet
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SellerAnalyticsPage;
